import React, { useCallback, useMemo, useRef, useState } from "react";
import api from "../utils/axios";
import { PagedResult } from "../api/client-reports/types";
import { Customs } from "../types/Lookups";
import { searchKey } from "../utils/searchKey";

// GET api/ListOptions/get-costums-paginated (see the API's
// docs/api-documentations/client/GET_ListOptions_Costums_Paginated.md).
const URL = "ListOptions/get-costums-paginated";
const PAGE_SIZE = 50;
/** The API takes up to 100 codes, and returns up to 100 rows, per request. */
const MAX_PER_REQUEST = 100;

/** How a customs row reads, in a dropdown and as a placeholder. */
export const customsLabel = (row: Customs) =>
	`${row.costumsCode} -  ${row.description}`;

/** A code as the API's search compares it: no spaces, dots or dashes. */
const codeKey = (text: unknown) =>
	String(text ?? "")
		.replace(/[\s.-]/g, "")
		.toLowerCase();

/**
 * The API's search, run in the browser on the rows already loaded: each word (the first five
 * different ones) must be in the description, accents and capitals ignored, or in the code,
 * spaces, dots and dashes ignored. Same rule as the server, so the list narrows while typing
 * without ever hiding a row the server finds.
 */
const customsMatch = (input: string, row: Customs) => {
	const words = Array.from(
		new Set(input.toLowerCase().split(/\s+/).filter(Boolean))
	).slice(0, 5);
	const description = searchKey(row.description);
	const code = codeKey(row.costumsCode);
	return words.every((word) => {
		const codeWord = codeKey(word);
		return (
			description.includes(searchKey(word)) ||
			(codeWord !== "" && code.includes(codeWord))
		);
	});
};

/** The rows of these exact codes (whitespace ignored), whatever their number. */
export const fetchCustomsByCodes = async (codes: string[]) => {
	const rows: Customs[] = [];
	for (let i = 0; i < codes.length; i += MAX_PER_REQUEST) {
		const chunk = codes.slice(i, i + MAX_PER_REQUEST).join(",");
		for (let currentPage = 1; ; currentPage++) {
			const response = await api.get<PagedResult<Customs>>(URL, {
				params: {
					codes: chunk,
					currentPage,
					pageSize: MAX_PER_REQUEST,
				},
				silent: true,
			});
			rows.push(...(response.data.items ?? []));
			if (currentPage >= response.data.totalPages) break;
		}
	}
	return rows;
};

/** A code as an exact lookup compares it: whitespace ignored, the Excel import's rule. */
export const customsCodeKey = (value: unknown) =>
	String(value ?? "").replace(/\s+/g, "");

/** Each of these codes' customs id, found by exact code; the first row of a code wins. */
export const fetchCustomsIds = async (codes: string[]) => {
	const ids = new Map<string, number>();
	for (const row of await fetchCustomsByCodes(codes)) {
		const code = customsCodeKey(row.costumsCode);
		if (!ids.has(code)) ids.set(code, row.id);
	}
	return ids;
};

/**
 * A searchable customs dropdown fed by the API: the first 50 codes when it first opens, the server
 * search 300 ms after the last key, the next 50 when the list is scrolled to the bottom. A response
 * that arrives after a newer request is ignored, and no next page loads while a search is on its
 * way. Call `select` from the dropdown's onSelect, which also fires when the selected row is picked
 * again (onChange does not).
 */
export function useCustomsOptions() {
	const [rows, setRows] = useState<Customs[]>([]);
	const [picked, setPicked] = useState<Customs>();
	const stateRef = useRef({
		search: "",
		page: 0,
		totalPages: 0,
		request: 0,
		loading: false,
	});
	const timer = useRef<ReturnType<typeof setTimeout>>();

	const load = useCallback(async (search: string, page: number) => {
		const state = stateRef.current;
		const request = ++state.request;
		state.loading = true;
		try {
			const response = await api.get<PagedResult<Customs>>(URL, {
				params: {
					search: search || undefined,
					currentPage: page,
					pageSize: PAGE_SIZE,
				},
				silent: true,
			});
			if (request !== state.request) return;
			Object.assign(state, {
				search,
				page,
				totalPages: response.data.totalPages,
			});
			const items = response.data.items ?? [];
			setRows((loaded) =>
				page === 1
					? items
					: [
							...loaded,
							...items.filter(
								(item) =>
									!loaded.some((row) => row.id === item.id)
							),
					  ]
			);
		} catch (error) {
			console.log(error);
		} finally {
			if (request === state.request) state.loading = false;
		}
	}, []);

	// The first page loads when the list first opens, not when the screen does.
	const onDropdownVisibleChange = (open: boolean) => {
		const state = stateRef.current;
		if (open && state.page === 0 && !state.loading) load("", 1);
	};

	const onSearch = (text: string) => {
		clearTimeout(timer.current);
		stateRef.current.loading = true;
		timer.current = setTimeout(
			() => load(text.trim().slice(0, 100), 1),
			300
		);
	};

	const onPopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const list = event.currentTarget;
		const { search, page, totalPages, loading } = stateRef.current;
		if (
			list.scrollTop + list.clientHeight >= list.scrollHeight - 20 &&
			page < totalPages &&
			!loading
		) {
			load(search, page + 1);
		}
	};

	// The picked row stays in the list, so its label survives a new search.
	const list = useMemo(
		() =>
			picked && !rows.some((row) => row.id === picked.id)
				? [picked, ...rows]
				: rows,
		[rows, picked]
	);

	const options = useMemo(
		() => list.map((row) => ({ value: row.id, label: customsLabel(row) })),
		[list]
	);

	const filterOption = (input: string, option?: { value?: unknown }) => {
		const row = list.find((item) => item.id === option?.value);
		return row ? customsMatch(input, row) : false;
	};

	/**
	 * Keeps the picked row's label. Picking empties the search box without a search event, so the
	 * list would keep this search's rows; the first page comes back for the next opening.
	 */
	const select = (id?: number | null) => {
		setPicked(list.find((row) => row.id === id));
		clearTimeout(timer.current);
		const state = stateRef.current;
		if (state.search || state.loading) load("", 1);
	};

	return {
		options,
		filterOption,
		onSearch,
		onPopupScroll,
		onDropdownVisibleChange,
		select,
	};
}
